// The score block inside the hand-end modal: the point total plus the named
// patterns that produced it (Classical only — Simplified never has a score).
// Split out of `HandEndModal.ts` when the verifier entry point pushed that file
// against the 200-line cap; nothing here changed in the move.

import { el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { HandScore, MatchedPattern } from '../scoring/types.js';

/**
 * Every `MatchedPattern.name` value produced by `scoring/patterns/*.ts` —
 * `scoring/types.ts` deliberately types `name` as plain `string` (patterns
 * are added file-by-file, not from a shared enum), so the display layer
 * keeps its own closed set here to type the i18n key lookup below. Keep in
 * sync with `scoring/patterns/*.ts`'s `name:` literals.
 */
type ScorePatternName =
  | 'wonHand'
  | 'selfDrawn'
  | 'meldPoints'
  | 'pairBonus'
  | 'sevenPairs'
  | 'allTriplets'
  | 'allSimples'
  | 'halfFlush'
  | 'fullFlush'
  | 'allHonors'
  | 'mixedTerminals'
  | 'pureTerminals'
  | 'greatDragons'
  | 'greatWinds'
  | 'thirteenOrphans'
  | 'heavenlyHand'
  | 'earthlyHand';

/** Base-point bookkeeping — not "named hands" a learner would recognize, so
 *  filtered out of the displayed pattern list (the point total already
 *  includes their contribution). */
const BOOKKEEPING_PATTERNS: ReadonlySet<string> = new Set([
  'wonHand',
  'selfDrawn',
  'meldPoints',
  'pairBonus',
]);

const SCORE_STYLE = [
  'display:flex',
  'flex-direction:column',
  'align-items:center',
  'gap:0.35rem',
].join(';');
const SCORE_POINTS_STYLE = 'margin:0;font-size:1.1rem;font-weight:600';
const SCORE_LIST_STYLE = [
  'margin:0',
  'padding:0',
  'list-style:none',
  'display:flex',
  'flex-direction:column',
  'align-items:center',
  'gap:0.15rem',
  'font-size:0.95rem',
  'opacity:0.9',
].join(';');
const SCORE_LIST_ITEM_STYLE = 'margin:0';

function namedPatterns(handScore: HandScore): readonly MatchedPattern[] {
  return handScore.patterns.filter((pattern) => !BOOKKEEPING_PATTERNS.has(pattern.name));
}

export function scoreSection(handScore: HandScore): HTMLElement {
  const patterns = namedPatterns(handScore);
  return el('div', { attrs: { style: SCORE_STYLE } }, [
    el('p', { text: t('score.points', { n: handScore.points }), attrs: { style: SCORE_POINTS_STYLE } }),
    patterns.length > 0
      ? el(
          'ul',
          { attrs: { style: SCORE_LIST_STYLE } },
          patterns.map((pattern) =>
            el('li', {
              text: t(`score.patterns.${pattern.name as ScorePatternName}`),
              attrs: { style: SCORE_LIST_ITEM_STYLE },
            }),
          ),
        )
      : null,
  ]);
}
